import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import Webcam from "react-webcam";
import { shuffleFromPositionOne } from "../../utils/shuffle-array";
import { setIntervalAsync } from "set-interval-async/dynamic";
import { clearIntervalAsync } from "set-interval-async";
import { faceLiveNessCheck, getBoundingBox } from "../../utils/face-liveness";
import { useStateRef } from "../../utils/custom-react-hook";
import delay from "../../utils/delay";
import Countdown from "react-countdown";
import { isMobile } from "react-device-detect";
import { Howl } from "howler";

let confirmAudio = new Howl({ src: ["confirm.wav"] });
let alertAudio = new Howl({ src: ["alert.mp3"] });

const faceVideoConstraints = {
  width: { ideal: 1280 },
  height: { ideal: isMobile ? 960 : 720 },
  facingMode: { ideal: "user" },
};

const faceActions = [
  { action: "forward", message: "Nhìn thẳng về phía máy ảnh" },
];

const getActionsSequence = () => {
  let sequence = shuffleFromPositionOne(faceActions);
  sequence.push(faceActions[0]);
  return sequence;
};

const VALID_FRAME = parseInt(process.env.REACT_APP_LIVENESS_VALID_FRAME);

// List of props: handleClose, handleAlert, faceDetection, onTakingImage
export default function FaceDetectionCamera(props) {
  const [mediaStream, setMediaStream] = React.useState(false);
  const [step, setStep, stepRef] = useStateRef(0);
  const validFrameCountRef = React.useRef(0);
  const cameraRef = React.useRef(null);

  const randomActionSequenceRef = React.useRef(getActionsSequence());
  const faceImageRef = React.useRef(null);
  const firstStepDelayRef = React.useRef(true);

  const handleOnPlay = () => {
    setMediaStream(true);
    const faceMesh = props.faceDetection;
    let setUpFaceDetectionCallBack = false;

    // Start an interval for face detecting
    const timer = setIntervalAsync(async () => {
      // Setting up callback for face detection for the first time
      if (!setUpFaceDetectionCallBack) {
        console.log("Start liveness check");
        setUpFaceDetectionCallBack = true;

        faceMesh.onResults(async (results) => {
          // Just to check if the countdown reset the step in-between the face liveness check
          let currentStep = stepRef.current;

          // Check if the user move the face outside of camera
          if (
            results.multiFaceLandmarks &&
            results.multiFaceLandmarks.length === 0 &&
            stepRef.current !== 0
          ) {
            firstStepDelayRef.current = true;
            alertAudio.play();
            validFrameCountRef.current = 0;
            randomActionSequenceRef.current = getActionsSequence();
            setStep(0);
          }
          // Check if the user does the required action for VALID_FRAME number of frame.
          else if (
            faceLiveNessCheck(
              results,
              randomActionSequenceRef.current[stepRef.current].action
            ) &&
            currentStep === stepRef.current
          ) {
            if (validFrameCountRef.current < VALID_FRAME) {
              validFrameCountRef.current += 1;
            } else {
              // If first step, taking the picture
              if (stepRef.current === 0) {
                const canvas = results.image;
                const { x1, x2, y1, y2 } = getBoundingBox(results);
                // Check if the face is fully presented
                if (
                  x1 >= 0 &&
                  y1 >= 0 &&
                  x2 <= canvas.width &&
                  y2 <= canvas.height
                ) {
                  faceImageRef.current = results.image.toDataURL("image/jpeg");
                  confirmAudio.play();
                  validFrameCountRef.current = 0;
                  setStep(stepRef.current + 1);
                }
              } else {
                if (
                  stepRef.current !==
                  randomActionSequenceRef.current.length - 1
                ) {
                  confirmAudio.play();
                  validFrameCountRef.current = 0;
                  setStep(stepRef.current + 1);
                } else {
                  props.onTakingImage(faceImageRef.current);
                  confirmAudio.play();
                  props.handleClose();
                  console.log("Stop liveness check");
                  clearIntervalAsync(timer);
                }
              }
            }
          }
          // Reset frame count when user failed the liveness check
          else if (currentStep === stepRef.current) {
            validFrameCountRef.current = 0;
          }
        });
      }

      // Start to process frame by frame
      if (
        cameraRef.current !== null &&
        cameraRef.current !== undefined &&
        cameraRef.current.video !== null &&
        cameraRef.current.video !== undefined
      ) {
        if (stepRef.current === 0 && firstStepDelayRef.current) {
          await delay(1000);
          firstStepDelayRef.current = false;
        }
        // Detect face, execute the callback function
        await faceMesh.send({ image: cameraRef.current.video });
      } else {
        // Close the interval when camera not detected
        console.log("Stop liveness check");
        clearIntervalAsync(timer);
      }
    }, 10);
  };

  const handleUserMediaError = () => {
    props.handleClose();
    props.handleAlert("Không kết nối được đến thiết bị chụp ảnh");
  };

  const onCountDownComplete = () => {
    firstStepDelayRef.current = true;
    alertAudio.play();
    randomActionSequenceRef.current = getActionsSequence();
    setStep(0);
    validFrameCountRef.current = 0;
  };

  const countDownRenderer = ({ seconds }) => {
    return (
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          width: isMobile ? "15%" : "10%",
          height: isMobile ? "10%" : "15%",
          display: "flex",
        }}
      >
        <Typography
          sx={{
            margin: "auto",
            color: "white",
          }}
          variant={isMobile ? "h4" : "h2"}
        >
          {seconds}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Webcam
          mirrored
          videoConstraints={faceVideoConstraints}
          screenshotFormat="image/jpeg"
          ref={cameraRef}
          onUserMediaError={handleUserMediaError}
          style={
            mediaStream
              ? {
                  width: "100%",
                  maxHeight: isMobile ? "70vh" : "80vh",
                  objectFit: "cover",
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block",
                  backgroundColor: "black",
                }
              : {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "1%",
                }
          }
          onPlay={handleOnPlay}
        />

        {mediaStream && (
          <Typography
            sx={{
              position: "absolute",
              bottom: "0",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              width: "100%",
              color: "white",
            }}
            variant={isMobile ? "h4" : "h2"}
            align="center"
          >
            {randomActionSequenceRef.current[step].message}
          </Typography>
        )}

        {step !== 0 && (
          <Countdown
            date={
              Date.now() + parseInt(process.env.REACT_APP_LIVENESS_COUNTDOWN)
            }
            onComplete={onCountDownComplete}
            renderer={countDownRenderer}
          />
        )}

        {!mediaStream && (
          <Box
            sx={{
              width: "100%",
              paddingBottom: isMobile ? "133.333333333%" : "56.25%",
              height: 0,
              backgroundColor: "black",
            }}
          />
        )}
      </Box>
      <LinearProgress
        sx={{ mt: 1 }}
        variant="determinate"
        value={
          (step * 100) / (randomActionSequenceRef.current.length - 1)
        }
      />
    </>
  );
}
